import {
  useEffect,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type FocusEvent,
} from "react";
import {
  UseComboboxProps as UseDownshiftComboboxLegacyProps,
  UseSelectProps as UseDownshiftSelectProps,
  UseComboboxReturnValue as UseDownshiftComboboxLegacyReturnValue,
  UseSelectReturnValue as UseDownshiftSelectReturnValue,
} from "downshift";
import cx from "classnames";

import { useComboboxLegacy } from "./ComboboxLegacy";

import { Popover } from "../Popover";

import styles from "./ComboboxLegacy.module.scss";

export type ComboboxLegacyContentOptionsProps<Item> = {
  optionProps: ReturnType<
    UseDownshiftComboboxLegacyReturnValue<Item>["getItemProps"]
  > & { selected: boolean; disabled: boolean };
  item: Item;
  index: number;
};

export type ComboboxLegacyContentProps<Item> = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> & {
  children?: ({
    items,
    inputValue,
    selectedItem,
    getMenuProps,
    getItemProps,
  }: Pick<
    UseDownshiftComboboxLegacyProps<Item> | UseDownshiftSelectProps<Item>,
    "items"
  > &
    Pick<
      | UseDownshiftComboboxLegacyReturnValue<Item>
      | UseDownshiftSelectReturnValue<Item>,
      "getMenuProps" | "getItemProps" | "inputValue" | "selectedItem"
    > & {
      listProps: ReturnType<
        | UseDownshiftComboboxLegacyReturnValue<Item>["getMenuProps"]
        | UseDownshiftSelectReturnValue<Item>["getMenuProps"]
      > & { style: any };
      options: ComboboxLegacyContentOptionsProps<Item>[];
    }) => ReactNode;
  disablePopover?: boolean;
};

export const ComboboxLegacyContent = function <Item = any>({
  children,
  className,
  disablePopover = false,
  ...rest
}: ComboboxLegacyContentProps<Item>) {
  const {
    items,
    itemToKey,
    inputValue,
    selectedItem,
    selectedItems,
    setHasDisabledPopover,
    getMenuProps,
    getItemProps,
    isDisabled,
    isReadOnly,
    referenceRef,
    contentRef,
    closeMenu,
  } = useComboboxLegacy<Item>();

  const ComboboxLegacyPopoverContentClassNames = cx(
    styles["popover-content"],
    className,
  );

  useEffect(() => {
    setHasDisabledPopover(disablePopover);

    return () => {
      setHasDisabledPopover(false);
    };
  }, [disablePopover, setHasDisabledPopover]);

  // if we focus out to something not within the reference or content divs
  // then close the popover
  const handleBlur = (e: FocusEvent) => {
    const target = e.relatedTarget ?? e.target;
    const focusedOnReference =
      referenceRef.current && referenceRef.current.contains(target);
    const focusedOnContent =
      contentRef.current && contentRef.current.contains(target);

    if (!focusedOnReference && !focusedOnContent) {
      closeMenu();
    }
  };

  return disablePopover ? (
    <div
      {...rest}
      className={ComboboxLegacyPopoverContentClassNames}
      ref={contentRef}
      onBlur={handleBlur}
    >
      {children
        ? children({
            items,
            inputValue,
            selectedItem,
            getMenuProps,
            getItemProps,
            listProps: {
              ...getMenuProps({}, { suppressRefError: true }),
              style: {
                display: items.length ? "" : "none",
                overflow: "auto",
              },
            },
            options: items.map((item, index) => ({
              optionProps: {
                ...getItemProps({
                  item,
                  index,
                }),

                selected:
                  itemToKey(selectedItem) === itemToKey(item) ||
                  selectedItems.some(
                    (selectedItem) =>
                      itemToKey(selectedItem) === itemToKey(item),
                  ),
                disabled: !!isDisabled || !!isReadOnly, // also see "isItemDisabled" function
              },
              item,
              index,
            })),
          })
        : null}
    </div>
  ) : (
    <Popover.Content
      data-anv="combobox-content"
      {...rest}
      className={ComboboxLegacyPopoverContentClassNames}
      ref={getMenuProps({ ref: contentRef }, { suppressRefError: true }).ref}
      onBlur={handleBlur}
    >
      {children
        ? children({
            items,
            inputValue,
            selectedItem,
            getMenuProps,
            getItemProps,
            listProps: {
              ...getMenuProps({}, { suppressRefError: true }),
              style: {
                display: items.length ? undefined : "none",
                overflow: "auto",
              },
            },
            options: items.map((item, index) => ({
              optionProps: {
                ...getItemProps({
                  item,
                  index,
                }),
                selected:
                  itemToKey(selectedItem) === itemToKey(item) ||
                  selectedItems.some(
                    (selectedItem) =>
                      itemToKey(selectedItem) === itemToKey(item),
                  ),
                disabled: !!isDisabled || !!isReadOnly, // also see "isItemDisabled" function
              },
              item,
              index,
            })),
          })
        : null}
    </Popover.Content>
  );
};
